/**
 * The three conversational views of the app (Chat, Research, Team Work) share one layout, as
 * they do in the desktop app: a strip naming the session and the branch, a hero with the
 * view's glow, three suggestion cards, and the composer at the foot with its picker, the
 * "work lands on" line and the send button.
 */

import { ArrowUp, BranchesRight } from '@gravity-ui/icons';
import { type ReactNode, useState } from 'react';

import { ArtHero, ArtSuggestion, type ArtTone } from '../../components/ui/Art.tsx';
import type { GitStatus, Workbench } from '../useWorkbench.ts';
import { ChoiceSelect } from '../../components/ui.tsx';

export interface Suggestion {
  tone: ArtTone;
  icon: ReactNode;
  text: string;
}

export function useBaseBranch(w: Workbench): string {
  const git = w.useView<GitStatus | null>('git.status', undefined, [], 120_000);
  return git.data?.base ?? git.data?.branches?.find((b) => b.kind === 'base')?.name ?? 'main';
}

export function WorkStrip({ label, session, branch }: { label: string; session: string; branch: string }) {
  return (
    <div className="flex h-10 shrink-0 items-center gap-2 border-b border-separator px-5 text-xs">
      <span className="pane-label">{label}</span>
      <span className="min-w-0 truncate text-muted">{session}</span>
      <span className="flex-1" />
      <span className="branch-pill">
        <BranchesRight className="size-3" />
        <span className="branch-pill__name">{branch}</span>
      </span>
    </div>
  );
}

export function WorkHero({ tone, title, blurb, suggestions, onPick }: { tone: ArtTone; title: string; blurb: string; suggestions: Suggestion[]; onPick: (text: string) => void }) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto px-6 py-8">
      <ArtHero tone={tone} icon={<img src="/mojoup-logo.svg" alt="" className="size-7" />} title={title} blurb={blurb}>
        <div className="grid w-full max-w-4xl gap-3 sm:grid-cols-3">
          {suggestions.map((s) => (
            <ArtSuggestion key={s.text} tone={s.tone} icon={s.icon} onPress={() => onPick(s.text)}>
              {s.text}
            </ArtSuggestion>
          ))}
        </div>
      </ArtHero>
    </div>
  );
}

/** The composer: Enter sends, Shift+Enter is a new line, pickers sit beside the send button. */
export function WorkComposer({
  text,
  setText,
  placeholder,
  lands,
  pickers,
  footnote,
  canSend,
  busy,
  onSend,
}: {
  text: string;
  setText: (t: string) => void;
  placeholder: string;
  lands: ReactNode;
  pickers: ReactNode;
  footnote: ReactNode;
  canSend: boolean;
  busy: boolean;
  onSend: () => void;
}) {
  return (
    <div className="shrink-0 px-6 pb-3">
      <div className="mx-auto w-full max-w-4xl">
        <div className="mb-2 text-center text-xs text-muted">{lands}</div>
        <div className="glass-card rounded-2xl p-3">
          <textarea
            className="min-h-16 w-full resize-none bg-transparent px-1 text-sm outline-none placeholder:text-muted"
            value={text}
            placeholder={placeholder}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (canSend && text.trim()) onSend();
              }
            }}
          />
          <div className="mt-2 flex items-center gap-2">
            {pickers}
            <span className="flex-1" />
            <button
              type="button"
              aria-label="Send"
              disabled={!canSend || !text.trim() || busy}
              onClick={onSend}
              className="flex size-10 items-center justify-center rounded-full bg-accent text-accent-foreground transition-opacity disabled:opacity-40"
            >
              <ArrowUp className="size-4" />
            </button>
          </div>
        </div>
        <div className="mt-2 text-center text-xs text-muted">{footnote}</div>
      </div>
    </div>
  );
}

export function Picker({ value, onChange, options, label }: { value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string; hint?: string }>; label: string }) {
  return (
    <ChoiceSelect label={label} value={value || undefined} options={Object.fromEntries(options.map((o) => [o.value, `${o.label}${o.hint ? ` · ${o.hint}` : ''}`]))} onChange={onChange} />
  );
}

export function useComposer() {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  return { text, setText, busy, setBusy };
}
